// Data Access Layer: User Track Selection
import { supabase } from '../supabase';
import type { UserTrackSelection, UserTrackSelectionWithTrack } from '../../types/database';

/**
 * Get current user ID (adapt for your auth provider)
 */
async function getCurrentUserId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id || null;
}

/**
 * Fetch the current user's track selection
 */
export async function getUserTrackSelection(): Promise<UserTrackSelectionWithTrack | null> {
  const userId = await getCurrentUserId();
  
  if (!userId) {
    return null;
  }

  const { data, error } = await supabase
    .from('user_track_selection')
    .select(`
      *,
      track:tracks(*)
    `)
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching user track selection:', error);
    throw new Error(`Failed to fetch track selection: ${error.message}`);
  }

  return data;
}

/**
 * Set the current user's track selection
 */
export async function setUserTrackSelection(
  trackId: string,
  targetTimeHorizonWeeks: number
): Promise<UserTrackSelection> {
  const userId = await getCurrentUserId();
  
  if (!userId) {
    throw new Error('No authenticated user');
  }

  const { data, error } = await supabase
    .from('user_track_selection')
    .upsert({
      user_id: userId,
      track_id: trackId,
      target_time_horizon_weeks: targetTimeHorizonWeeks,
    })
    .select()
    .single();

  if (error) {
    console.error('Error setting user track selection:', error);
    throw new Error(`Failed to set track selection: ${error.message}`);
  }

  return data;
}

/**
 * Remove the current user's track selection
 */
export async function removeUserTrackSelection(): Promise<void> {
  const userId = await getCurrentUserId();
  
  if (!userId) {
    throw new Error('No authenticated user');
  }

  const { error } = await supabase
    .from('user_track_selection')
    .delete()
    .eq('user_id', userId);

  if (error) {
    console.error('Error removing user track selection:', error);
    throw new Error(`Failed to remove track selection: ${error.message}`);
  }
}
